/**
 * Developer landing pages: /developer/<slug>, hub at /developers.
 *
 * People search developers by name ("Emaar off plan", "مشاريع داماك",
 * "Binghatti new projects"), and before these pages the only answer the site
 * had was a scatter of project pages. Each developer now gets one page that
 * lists all of its projects, with copy computed from the live feed — count,
 * starting and median price, the areas it builds in, handover years — so the
 * text is unique per developer and never goes stale.
 *
 * The developer list itself is derived from the feed at build time; React
 * reads it from /data/developers.json. Shared by React and the prerenderer.
 */

import { normaliseProject } from './projectSeo.js';
import { areaStats } from './areas.js';
import { compactPrice, fullPrice, projectCount, listJoin, districtLabel, slugify } from './seoCopy.js';

/** Fewer live projects than this and a developer page would be thin. */
export const MIN_DEVELOPER_PROJECTS = 3;

export const developerSlug = (name) => slugify(name);

/**
 * Groups projects by developer: [{ slug, name, rows }], busiest first.
 * Two spellings that slug alike ("DAMAC", "Damac") are one developer.
 */
export function groupByDeveloper(rows) {
    const map = new Map();
    for (const raw of rows) {
        const name = String(raw?.developer_name || '').trim();
        if (!name || !raw?.id || !raw?.title) continue;
        const slug = developerSlug(name);
        if (!map.has(slug)) map.set(slug, { slug, name, rows: [] });
        map.get(slug).rows.push(raw);
    }
    return [...map.values()].sort((a, b) => b.rows.length - a.rows.length || a.name.localeCompare(b.name));
}

/** areaStats plus the districts the developer builds in, busiest first. */
export function developerStats(rows) {
    const s = areaStats(rows);
    const counts = new Map();
    for (const raw of rows) {
        const d = districtLabel(normaliseProject(raw).district);
        if (d) counts.set(d, (counts.get(d) || 0) + 1);
    }
    return { ...s, districts: [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([name, n]) => ({ name, n })) };
}

export const developerIsIndexable = (s) => s.liveCount >= MIN_DEVELOPER_PROJECTS;

const years = (s) => (!s.handoverFrom ? '' : s.handoverFrom === s.handoverTo ? String(s.handoverFrom) : `${s.handoverFrom}–${s.handoverTo}`);

/** <title>, 60 characters at most. */
export function developerTitle(dev, s, lang = 'ar') {
    const n = projectCount(s.liveCount, lang);
    const build = (from) => ({
        ar: `مشاريع ${dev.name} على الخارطة | ${n}${from ? ` من ${from}` : ''}`,
        en: `${dev.name} Off-Plan Projects | ${n}${from ? ` from ${from}` : ''}`,
        ru: `Новостройки ${dev.name}: ${n}${from ? ` от ${from}` : ''}`,
        zh: `${dev.name}期房项目｜${n}${from ? `，${from}起` : ''}`,
        ur: `${dev.name} کے آف پلان پروجیکٹس | ${n}${from ? `، ${from} سے` : ''}`
    }[lang]);
    const full = build(compactPrice(s.priceFrom, lang));
    return full.length <= 60 ? full : build(null);
}

/** Meta description: fact-first, unique per developer, ≤158 characters. */
export function developerDescription(dev, s, lang = 'ar') {
    const from = fullPrice(s.priceFrom, lang);
    const areas = listJoin(s.districts.slice(0, 3).map((d) => d.name), lang);
    const n = projectCount(s.liveCount, lang);
    const parts = {
        ar: [`مشاريع ${dev.name} على الخارطة: ${n} بأسعار المطوّر مباشرة.`, from && `تبدأ من ${from}.`, areas && `في ${areas}.`, 'بدون عمولة على المشتري.'],
        en: [`${dev.name} off-plan projects: ${n} at direct developer prices.`, from && `From ${from}.`, areas && `In ${areas}.`, 'Zero buyer commission.'],
        ru: [`Новостройки ${dev.name}: ${n} по ценам застройщика.`, from && `От ${from}.`, areas && `Районы: ${areas}.`, 'Без комиссии для покупателя.'],
        zh: [`${dev.name}期房项目：${n}，开发商直售价。`, from && `${from}起。`, areas && `区域：${areas}。`, '买家零佣金。'],
        ur: [`${dev.name} کے آف پلان پروجیکٹس: ${n}، براہِ راست ڈیولپر قیمت پر۔`, from && `${from} سے شروع۔`, areas && `علاقے: ${areas}۔`, 'خریدار سے کوئی کمیشن نہیں۔']
    }[lang] || [];
    const sep = lang === 'zh' ? '' : ' ';
    let out = '';
    for (const part of parts.filter(Boolean)) {
        const next = out ? `${out}${sep}${part}` : part;
        if (next.length > 158) break;
        out = next;
    }
    return out;
}

/** The direct-answer paragraph that opens the page. */
export function developerAnswer(dev, s, lang = 'ar') {
    const from = fullPrice(s.priceFrom, lang);
    const median = fullPrice(s.priceMedian, lang);
    const cheapest = s.live[0];
    const areas = listJoin(s.districts.slice(0, 4).map((d) => d.name), lang);
    const y = years(s);
    const n = projectCount(s.liveCount, lang);
    const lines = {
        ar: [
            `يطرح ${dev.name} حالياً ${n} على الخارطة متاحاً للبيع بأسعار المطوّر مباشرة.`,
            areas && `تتوزّع مشاريعه في ${areas}.`,
            from && cheapest && `تبدأ الأسعار من ${from} في مشروع ${cheapest.title}، ويبلغ متوسط سعر البداية ${median}.`,
            y && `مواعيد التسليم المتوقّعة بين ${y}.`,
            'نبيع مشاريعه بسعر المطوّر الرسمي دون أي عمولة على المشتري.'
        ],
        en: [
            `${dev.name} currently has ${n} on sale off-plan, all at direct developer prices.`,
            areas && `Its projects are in ${areas}.`,
            from && cheapest && `Prices start from ${from} at ${cheapest.title}, with a median starting price of ${median}.`,
            y && `Expected handovers run ${y}.`,
            'We sell its projects at the official developer price with zero buyer commission.'
        ],
        ru: [
            `У застройщика ${dev.name} сейчас в продаже ${n} на этапе строительства — по ценам застройщика.`,
            areas && `Районы: ${areas}.`,
            from && cheapest && `Цены начинаются от ${from} в проекте ${cheapest.title}; медианная стартовая цена — ${median}.`,
            y && `Ожидаемые сроки сдачи: ${y}.`,
            'Мы продаём его проекты по официальной цене застройщика без комиссии для покупателя.'
        ],
        zh: [
            `${dev.name}目前有${n}期房在售，均为开发商直售价。`,
            areas && `项目分布于${areas}。`,
            from && cheapest && `价格从${cheapest.title}的${from}起，起价中位数为${median}。`,
            y && `预计交付时间为${y}年。`,
            '我们按开发商官方价格销售其项目，买家零佣金。'
        ],
        ur: [
            `${dev.name} کے اس وقت ${n} آف پلان فروخت کے لیے دستیاب ہیں، براہِ راست ڈیولپر قیمت پر۔`,
            areas && `اس کے پروجیکٹس ${areas} میں ہیں۔`,
            from && cheapest && `قیمتیں ${cheapest.title} میں ${from} سے شروع ہوتی ہیں، اور ابتدائی قیمت کا درمیانہ ${median} ہے۔`,
            y && `متوقع حوالگی: ${y}۔`,
            'ہم اس کے پروجیکٹس ڈیولپر کی سرکاری قیمت پر، خریدار سے کوئی کمیشن لیے بغیر فروخت کرتے ہیں۔'
        ]
    }[lang] || [];
    return lines.filter(Boolean).join(lang === 'zh' ? '' : ' ');
}

/** FAQ from the developer's live data. */
export function developerFaq(dev, s, lang = 'ar') {
    const from = fullPrice(s.priceFrom, lang);
    const cheap = s.live.slice(0, 3);
    const y = years(s);
    const n = projectCount(s.liveCount, lang);
    const areaList = (sep) => s.districts.slice(0, 6).map((d) => `${d.name} (${d.n})`).join(sep);
    const cheapList = (sep, fmt) => cheap.map((p) => fmt(p.title, fullPrice(p.priceMin, lang))).join(sep);
    const name = dev.name;

    const Q = {
        ar: {
            price: [`كم أسعار مشاريع ${name} على الخارطة؟`, `تبدأ أسعار مشاريع ${name} على الخارطة من ${from} (عدد المشاريع المتاحة: ${n}). يختلف السعر حسب المشروع ونوع الوحدة والطابق، والأسعار المعروضة هي أسعار المطوّر الرسمية.`],
            areas: [`أين تقع مشاريع ${name}؟`, `تتوزّع مشاريع ${name} الحالية في: ${areaList('، ')}.`],
            cheap: [`ما أرخص مشاريع ${name} حالياً؟`, `أقل مشاريع ${name} سعراً حالياً: ${cheapList('، ', (t, p) => `${t} من ${p}`)}.`],
            handover: [`متى تُسلَّم مشاريع ${name}؟`, `تتراوح مواعيد التسليم المتوقّعة لمشاريع ${name} المتاحة بين ${y}.`]
        },
        en: {
            price: [`How much do ${name} off-plan projects cost?`, `${name} off-plan projects start from ${from} (${n} on sale). Prices vary by project, unit type and floor; the prices shown are official developer prices.`],
            areas: [`Where are ${name} projects located?`, `${name}'s current projects are in: ${areaList(', ')}.`],
            cheap: [`What are the cheapest ${name} projects right now?`, `The lowest-priced ${name} projects right now: ${cheapList(', ', (t, p) => `${t} from ${p}`)}.`],
            handover: [`When do ${name} projects hand over?`, `Expected handovers for ${name} projects on sale run ${y}.`]
        },
        ru: {
            price: [`Сколько стоят новостройки ${name}?`, `Новостройки ${name} стоят от ${from} (в продаже: ${n}). Цена зависит от проекта, типа юнита и этажа; указаны официальные цены застройщика.`],
            areas: [`Где находятся проекты ${name}?`, `Текущие проекты ${name}: ${areaList(', ')}.`],
            cheap: [`Какие проекты ${name} самые доступные?`, `Самые доступные проекты ${name} сейчас: ${cheapList('; ', (t, p) => `${t} — от ${p}`)}.`],
            handover: [`Когда сдаются проекты ${name}?`, `Ожидаемые сроки сдачи проектов ${name}: ${y}.`]
        },
        zh: {
            price: [`${name}期房项目价格是多少？`, `${name}期房项目${from}起（在售${n}）。价格因项目、户型和楼层而异，所示均为开发商官方价格。`],
            areas: [`${name}的项目位于哪里？`, `${name}目前的项目分布：${areaList('、')}。`],
            cheap: [`${name}目前最便宜的项目有哪些？`, `${name}目前价格最低的项目：${cheapList('；', (t, p) => `${t}，${p}起`)}。`],
            handover: [`${name}的项目何时交付？`, `${name}在售项目的预计交付时间为${y}年。`]
        },
        ur: {
            price: [`${name} کے آف پلان پروجیکٹس کی قیمت کیا ہے؟`, `${name} کے آف پلان پروجیکٹس ${from} سے شروع ہوتے ہیں (دستیاب: ${n})۔ قیمت پروجیکٹ، یونٹ کی قسم اور منزل کے مطابق بدلتی ہے؛ دکھائی گئی قیمتیں ڈیولپر کی سرکاری قیمتیں ہیں۔`],
            areas: [`${name} کے پروجیکٹس کہاں ہیں؟`, `${name} کے موجودہ پروجیکٹس: ${areaList('، ')}۔`],
            cheap: [`${name} کے سب سے سستے پروجیکٹس کون سے ہیں؟`, `${name} کے اس وقت سب سے کم قیمت پروجیکٹس: ${cheapList('، ', (t, p) => `${t} — ${p} سے`)}۔`],
            handover: [`${name} کے پروجیکٹس کی حوالگی کب ہے؟`, `${name} کے دستیاب پروجیکٹس کی متوقع حوالگی ${y} کے درمیان ہے۔`]
        }
    }[lang] || {};

    const out = [];
    if (from && Q.price) out.push(Q.price);
    if (s.districts.length && Q.areas) out.push(Q.areas);
    if (cheap.length >= 2 && Q.cheap) out.push(Q.cheap);
    if (y && Q.handover) out.push(Q.handover);
    return out.map(([q, a]) => ({ q, a }));
}

/** Headings and labels of developer pages, shared by React and the prerenderer. */
const LABELS = {
    ar: {
        h1: (d) => `مشاريع ${d.name} على الخارطة`, about: (d) => `عن ${d.name}`, list: (d, n) => `كل مشاريع ${d.name} (${n})`,
        faq: (d) => `أسئلة شائعة عن ${d.name}`, cta: (d) => `اطلب قائمة أسعار ${d.name} المحدّثة`, others: 'مطوّرون آخرون', all: 'كل المطوّرين',
        link: (d) => `مشاريع ${d.name}`, notFound: 'المطوّر غير موجود', projects: (n) => projectCount(n, 'ar'),
        indexTitle: 'المطوّرون العقاريون في دبي وأبوظبي | مشاريعهم على الخارطة', indexDescription: 'كل المطوّرين ومشاريعهم على الخارطة: Emaar وDAMAC وSobha وAzizi وBinghatti وغيرهم، بأسعار المطوّر مباشرة وبدون عمولة.',
        indexH1: 'المطوّرون العقاريون ومشاريعهم على الخارطة', indexAnswer: 'اختر المطوّر لترى كل مشاريعه المتاحة على الخارطة، وأقل الأسعار، والمناطق التي يبني فيها، ومواعيد التسليم. الأرقام محدّثة من بيانات المطوّرين.'
    },
    en: {
        h1: (d) => `${d.name} Off-Plan Projects`, about: (d) => `About ${d.name}`, list: (d, n) => `All ${d.name} projects (${n})`,
        faq: (d) => `${d.name} FAQ`, cta: (d) => `Get the latest ${d.name} price list`, others: 'Other developers', all: 'All developers',
        link: (d) => `${d.name} projects`, notFound: 'Developer not found', projects: (n) => projectCount(n, 'en'),
        indexTitle: 'Dubai & Abu Dhabi Developers | Off-Plan Projects', indexDescription: 'Every developer and its off-plan projects — Emaar, DAMAC, Sobha, Azizi, Binghatti and more — at direct developer prices with zero commission.',
        indexH1: 'Developers and their off-plan projects', indexAnswer: 'Pick a developer to see all of its off-plan projects on sale, the lowest prices, the areas it builds in and expected handover dates. Figures are refreshed from developer data.'
    },
    ru: {
        h1: (d) => `Новостройки ${d.name}`, about: (d) => `О застройщике ${d.name}`, list: (d, n) => `Все проекты ${d.name} (${n})`,
        faq: (d) => `Вопросы о ${d.name}`, cta: (d) => `Получить актуальный прайс ${d.name}`, others: 'Другие застройщики', all: 'Все застройщики',
        link: (d) => `Проекты ${d.name}`, notFound: 'Застройщик не найден', projects: (n) => projectCount(n, 'ru'),
        indexTitle: 'Застройщики Дубая и Абу-Даби | Новостройки', indexDescription: 'Все застройщики и их строящиеся проекты — Emaar, DAMAC, Sobha, Azizi, Binghatti и другие — по ценам застройщика без комиссии.',
        indexH1: 'Застройщики и их новостройки', indexAnswer: 'Выберите застройщика, чтобы увидеть все его проекты в продаже, минимальные цены, районы и сроки сдачи. Данные обновляются из информации застройщиков.'
    },
    zh: {
        h1: (d) => `${d.name}期房项目`, about: (d) => `关于${d.name}`, list: (d, n) => `${d.name}全部项目（${n}）`,
        faq: (d) => `${d.name}常见问题`, cta: (d) => `获取${d.name}最新价目表`, others: '其他开发商', all: '全部开发商',
        link: (d) => `${d.name}项目`, notFound: '未找到该开发商', projects: (n) => projectCount(n, 'zh'),
        indexTitle: '迪拜与阿布扎比开发商｜期房项目', indexDescription: '所有开发商及其期房项目 — Emaar、DAMAC、Sobha、Azizi、Binghatti等 — 开发商直售价，零佣金。',
        indexH1: '开发商及其期房项目', indexAnswer: '选择开发商即可查看其全部在售期房项目、最低价格、开发区域和预计交付时间。数据根据开发商信息更新。'
    },
    ur: {
        h1: (d) => `${d.name} کے آف پلان پروجیکٹس`, about: (d) => `${d.name} کے بارے میں`, list: (d, n) => `${d.name} کے تمام پروجیکٹس (${n})`,
        faq: (d) => `${d.name} — عمومی سوالات`, cta: (d) => `${d.name} کی تازہ قیمتوں کی فہرست حاصل کریں`, others: 'دیگر ڈیولپرز', all: 'تمام ڈیولپرز',
        link: (d) => `${d.name} کے پروجیکٹس`, notFound: 'ڈیولپر نہیں ملا', projects: (n) => projectCount(n, 'ur'),
        indexTitle: 'دبئی اور ابوظہبی کے ڈیولپرز | آف پلان پروجیکٹس', indexDescription: 'تمام ڈیولپرز اور ان کے آف پلان پروجیکٹس — Emaar، DAMAC، Sobha، Azizi، Binghatti اور دیگر — براہِ راست ڈیولپر قیمت پر، کوئی کمیشن نہیں۔',
        indexH1: 'ڈیولپرز اور ان کے آف پلان پروجیکٹس', indexAnswer: 'ڈیولپر منتخب کریں اور اس کے تمام دستیاب آف پلان پروجیکٹس، سب سے کم قیمتیں، علاقے اور متوقع حوالگی دیکھیں۔ اعداد و شمار ڈیولپرز کے ڈیٹا سے تازہ کیے جاتے ہیں۔'
    }
};
export const developerLabels = (lang) => LABELS[lang] || LABELS.en;
